import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Database } from '@/types/database.generated';

export type GroupSessionRow = Database['public']['Tables']['group_sessions']['Row'];
export type GroupSessionStatus = Database['public']['Enums']['group_session_status'];
export type GroupParticipantRow =
  Database['public']['Tables']['group_session_participants']['Row'];
export type GroupParticipantStatus = Database['public']['Enums']['group_participant_status'];

export interface GroupSessionListItem {
  id: string;
  clinic_id: string;
  name: string;
  description: string | null;
  starts_at: string;
  ends_at: string;
  duration_minutes: number;
  max_participants: number;
  min_participants: number;
  current_participants: number;
  price_per_spot_cents: number;
  currency: string;
  color: string;
  clinician_id: string | null;
  clinician_name: string | null;
  status: GroupSessionStatus;
  participant_count: number;
}

export interface GroupSessionParticipantItem {
  id: string;
  group_session_id: string;
  patient_id: string;
  patient_name: string;
  patient_email: string | null;
  spots: number;
  status: GroupParticipantStatus;
  payment_status: string | null;
  payment_amount_cents: number | null;
  notes: string | null;
  registered_at: string;
}

export interface ListFilters {
  start: string; // ISO
  end: string; // ISO
  clinicianId?: string | null;
}

export const groupSessionsRangeQueryKey = (clinicId: string | null, filters: ListFilters) =>
  ['group_sessions', 'range', clinicId, filters] as const;

export const groupSessionDetailQueryKey = (id: string | null | undefined) =>
  ['group_sessions', 'detail', id] as const;

export const groupSessionParticipantsQueryKey = (sessionId: string | null | undefined) =>
  ['group_session_participants', sessionId] as const;

/**
 * Fetch group sessions in a date range for calendar display
 */
export function useGroupSessionsInRange(
  clinicId: string | null | undefined,
  filters: ListFilters,
) {
  return useQuery({
    queryKey: groupSessionsRangeQueryKey(clinicId ?? null, filters),
    enabled: Boolean(clinicId && filters.start && filters.end),
    queryFn: async (): Promise<GroupSessionListItem[]> => {
      if (!clinicId) return [];

      const { data, error } = await supabase.rpc('get_group_sessions_in_range', {
        p_clinic_id: clinicId,
        p_start: filters.start,
        p_end: filters.end,
        p_clinician_id: filters.clinicianId ?? undefined,
      });

      if (error) throw error;
      return (data ?? []).map((item: unknown) => item as GroupSessionListItem);
    },
    staleTime: 15_000,
  });
}

/**
 * Fetch a single group session by ID
 */
export function useGroupSession(id: string | null | undefined) {
  return useQuery({
    queryKey: groupSessionDetailQueryKey(id ?? null),
    enabled: Boolean(id),
    queryFn: async (): Promise<GroupSessionRow> => {
      if (!id) throw new Error('No id');
      const { data, error } = await supabase.from('group_sessions').select('*').eq('id', id).single();
      if (error) throw error;
      return data as GroupSessionRow;
    },
  });
}

/**
 * Fetch participants for a group session
 */
export function useGroupSessionParticipants(sessionId: string | null | undefined) {
  return useQuery({
    queryKey: groupSessionParticipantsQueryKey(sessionId ?? null),
    enabled: Boolean(sessionId),
    queryFn: async (): Promise<GroupSessionParticipantItem[]> => {
      if (!sessionId) return [];

      const { data, error } = await supabase
        .from('group_session_participants')
        .select(
          'id, group_session_id, patient_id, spots, status, payment_status, payment_amount_cents, notes, registered_at, ' +
            'patient:patients!group_session_participants_patient_id_fkey(first_name,last_name,email)',
        )
        .eq('group_session_id', sessionId)
        .order('registered_at', { ascending: true });

      if (error) throw error;

      type Joined = {
        id: string;
        group_session_id: string;
        patient_id: string;
        spots: number;
        status: GroupParticipantStatus;
        payment_status: string | null;
        payment_amount_cents: number | null;
        notes: string | null;
        registered_at: string;
        patient: { first_name: string; last_name: string; email: string | null } | null;
      };

      return ((data ?? []) as unknown as Joined[]).map(
        (r): GroupSessionParticipantItem => ({
          id: r.id,
          group_session_id: r.group_session_id,
          patient_id: r.patient_id,
          patient_name: r.patient ? `${r.patient.first_name} ${r.patient.last_name}`.trim() : '—',
          patient_email: r.patient?.email ?? null,
          spots: r.spots,
          status: r.status,
          payment_status: r.payment_status,
          payment_amount_cents: r.payment_amount_cents,
          notes: r.notes,
          registered_at: r.registered_at,
        }),
      );
    },
  });
}

/**
 * Realtime subscription to group sessions
 */
export function useGroupSessionsRealtime(
  clinicId: string | null | undefined,
  filters: ListFilters,
) {
  const qc = useQueryClient();
  const queryKey = groupSessionsRangeQueryKey(clinicId ?? null, filters);

  useEffect(() => {
    if (!clinicId) return;

    const channel = supabase
      .channel('group_sessions_realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'group_sessions',
          filter: `clinic_id=eq.${clinicId}`,
        },
        () => {
          qc.invalidateQueries({ queryKey });
        },
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'group_session_participants',
          filter: `clinic_id=eq.${clinicId}`,
        },
        () => {
          qc.invalidateQueries({ queryKey });
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [clinicId, qc, queryKey]);
}

/**
 * Create a new group session
 */
export interface CreateGroupSessionInput {
  clinic_id: string;
  name: string;
  description?: string | null;
  starts_at: string;
  ends_at: string;
  duration_minutes: number;
  max_participants: number;
  min_participants?: number;
  price_per_spot_cents: number;
  currency?: string;
  color?: string;
  clinician_id?: string | null;
  notes?: string | null;
}

export function useCreateGroupSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateGroupSessionInput): Promise<GroupSessionRow> => {
      const { data, error } = await supabase
        .from('group_sessions')
        .insert({
          clinic_id: input.clinic_id,
          name: input.name,
          description: input.description,
          starts_at: input.starts_at,
          ends_at: input.ends_at,
          duration_minutes: input.duration_minutes,
          max_participants: input.max_participants,
          min_participants: input.min_participants ?? 1,
          price_per_spot_cents: input.price_per_spot_cents,
          currency: input.currency ?? 'DKK',
          color: input.color ?? '#6366F1',
          clinician_id: input.clinician_id,
          notes: input.notes,
        })
        .select()
        .single();

      if (error) throw error;
      return data as GroupSessionRow;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['group_sessions'] });
    },
  });
}

/**
 * Update an existing group session
 */
export interface UpdateGroupSessionInput {
  id: string;
  name?: string;
  description?: string | null;
  starts_at?: string;
  ends_at?: string;
  duration_minutes?: number;
  max_participants?: number;
  min_participants?: number;
  price_per_spot_cents?: number;
  color?: string;
  clinician_id?: string | null;
  status?: GroupSessionStatus;
  cancellation_reason?: string | null;
  notes?: string | null;
}

export function useUpdateGroupSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateGroupSessionInput): Promise<GroupSessionRow> => {
      const { id, ...updates } = input;
      const { data, error } = await supabase
        .from('group_sessions')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as GroupSessionRow;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['group_sessions'] });
      qc.invalidateQueries({ queryKey: groupSessionDetailQueryKey(row.id) });
    },
  });
}

/**
 * Delete a group session
 */
export function useDeleteGroupSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (sessionId: string): Promise<void> => {
      const { error } = await supabase.from('group_sessions').delete().eq('id', sessionId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['group_sessions'] });
    },
  });
}

/**
 * Add a participant to a group session
 */
export interface AddParticipantInput {
  clinic_id: string;
  group_session_id: string;
  patient_id: string;
  spots?: number;
  payment_status?: string | null;
  payment_amount_cents?: number | null;
  payment_currency?: string | null;
  payment_method?: string | null;
  notes?: string | null;
}

export function useAddParticipant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AddParticipantInput): Promise<GroupParticipantRow> => {
      const { data, error } = await supabase
        .from('group_session_participants')
        .insert({
          clinic_id: input.clinic_id,
          group_session_id: input.group_session_id,
          patient_id: input.patient_id,
          spots: input.spots ?? 1,
          payment_status: (input.payment_status ?? null) as Database['public']['Enums']['payment_status'] | null,
          payment_amount_cents: input.payment_amount_cents,
          payment_currency: input.payment_currency,
          payment_method: input.payment_method,
          notes: input.notes,
        })
        .select()
        .single();

      if (error) throw error;
      return data as GroupParticipantRow;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['group_sessions'] });
      qc.invalidateQueries({ queryKey: groupSessionParticipantsQueryKey(row.group_session_id) });
      qc.invalidateQueries({ queryKey: groupSessionDetailQueryKey(row.group_session_id) });
    },
  });
}

/**
 * Remove a participant from a group session
 */
export function useRemoveParticipant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      participantId,
    }: {
      participantId: string;
      sessionId: string;
    }): Promise<void> => {
      const { error } = await supabase
        .from('group_session_participants')
        .delete()
        .eq('id', participantId);
      if (error) throw error;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['group_sessions'] });
      qc.invalidateQueries({ queryKey: groupSessionParticipantsQueryKey(vars.sessionId) });
      qc.invalidateQueries({ queryKey: groupSessionDetailQueryKey(vars.sessionId) });
    },
  });
}

/**
 * Update participant status
 */
export interface UpdateParticipantInput {
  participantId: string;
  sessionId: string;
  status?: GroupParticipantStatus;
  payment_status?: string | null;
  notes?: string | null;
}

export function useUpdateParticipant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateParticipantInput): Promise<GroupParticipantRow> => {
      const { participantId, sessionId, payment_status, ...rest } = input;
      const updates = { ...rest, payment_status: (payment_status ?? null) as Database['public']['Enums']['payment_status'] | null };
      const { data, error } = await supabase
        .from('group_session_participants')
        .update(updates)
        .eq('id', participantId)
        .select()
        .single();

      if (error) throw error;
      return data as GroupParticipantRow;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['group_sessions'] });
      qc.invalidateQueries({ queryKey: groupSessionParticipantsQueryKey(vars.sessionId) });
      qc.invalidateQueries({ queryKey: groupSessionDetailQueryKey(vars.sessionId) });
    },
  });
}
